"use client";

import * as Sentry from "@sentry/nextjs";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { Component, ErrorInfo, ReactNode } from "react";

import { handleError } from "@/shared/api/error-handler";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  /**
   * Имя блока, который защищает граница. Уходит в отчёт: без него в GlitchTip
   * видно «упало что-то на странице», а не «упала лента матчей» (KAN-994).
   */
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Централизованный pipeline (POST /crash-reports + toast) — сохраняем
    handleError(error, {
      component: this.props.name ?? "ErrorBoundary",
      action: "componentDidCatch",
      meta: { errorInfo: errorInfo.componentStack },
    });

    // Параллельная отправка в GlitchTip с полным контекстом компонента
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        stage: "react_error",
      },
      tags: {
        stage: "react_error",
        error_code: "REACT_BOUNDARY",
        ...(this.props.name ? { widget: this.props.name } : {}),
      },
    });

    // Вызываем кастомный обработчик
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorBoundaryFallback
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          showDetails={this.props.showDetails}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorBoundaryFallback({
  onRetry,
  onGoHome,
  showDetails,
  error,
  errorInfo,
}: {
  onRetry: () => void;
  onGoHome: () => void;
  showDetails?: boolean | undefined;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}) {
  const tCommon = useTranslations("Common");
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-xl">
            {tCommon("unexpectedErrorTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-muted-foreground">
            {tCommon("unexpectedErrorDescription")}
          </p>

          {showDetails && error && (
            <details className="rounded-md bg-muted p-3">
              <summary className="cursor-pointer text-sm font-medium">
                Детали ошибки
              </summary>
              <div className="mt-2 text-xs text-muted-foreground">
                <p>
                  <strong>Ошибка:</strong> {error.message}
                </p>
                {errorInfo && (
                  <p>
                    <strong>Компонент:</strong> {errorInfo.componentStack}
                  </p>
                )}
              </div>
            </details>
          )}

          <div className="flex gap-2">
            <Button onClick={onRetry} className="flex-1">
              <RefreshCw className="mr-2 h-4 w-4" />
              {tCommon("tryAgain")}
            </Button>
            <Button variant="outline" onClick={onGoHome}>
              <Home className="mr-2 h-4 w-4" />
              {tCommon("goHome")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// HOC для обертки компонентов
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, "children">,
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}

// Хук для обработки ошибок в функциональных компонентах
export function useErrorHandler() {
  const handleErrorCallback = React.useCallback(
    (error: unknown, context?: string) => {
      handleError(error, {
        component: context || "component",
        action: "useErrorHandler",
      });
    },
    [],
  );

  return { handleError: handleErrorCallback };
}
